// Global Instructions Rule Applied!
//
// The fund Ledger tab's Close year / Reopen year confirm
// (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md 4.2, PR 2b). Closing a year of
// a fund on the Financial year basis books the year's partner allocation
// first - per partner and GL account, the curative rule per K-1 tax category
// - so the dialog shows what the close will book before it books it:
//   * one row per partner, a Calculated / Override pair per tax category
//     (the pair the capital call and distribution forms use: Calculated
//     read-only, Override an InputNumber empty unless overridden, the
//     calculated amount struck through once overridden, clearing the input
//     is the undo), with a total row per category that must read the
//     category's total - Close year is disabled with its reason while the
//     overrides do not keep it there (judged against the RPC's own
//     balance, never by re-summing the rounded cells - backlog 1.22), or
//     while a P&L account with a balance has no tax category;
//   * the carry-forward (`allocation_outstanding`): what this year's items
//     could not cure, per partner and category, cured by the next year's
//     items - a warning, never a block;
//   * a fund on Static: the plain confirm of before (the close books no
//     allocation).
// Reopen: the plain confirm; the server removes the closing lines and the
// allocation lines the close wrote.
//
// Lightweight fetch-and-render (LedgerTab's recipe): the dialog loads the
// read when it opens, keeps the overrides in local state and sends them with
// the close in the request envelope. No redux slice.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, InputNumber, Modal, Table, Tooltip, Typography } from 'antd'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { useNumberDisplay, useNumberFormat } from '../../../../../core/utils/inputNumberFormat.ts'
import { fetchFundFinYearAllocation } from '../../../services/fundsService.ts'
import type {
  FinYearAllocationCategory,
  FinYearAllocationCell,
  FinYearAllocationOverride,
  FinYearAllocationRow,
  FundFinYearAllocation
} from '../../../../../state_manager/data_models/fund_fin_year_allocation.ts'

const { Text } = Typography

export const CALCULATED_TITLE = 'Calculated'
export const OVERRIDE_TITLE = 'Override'

interface CloseFinYearModalProps {
  open: boolean
  fundId: string
  finYear: { id: string; fin_year: number; closed?: boolean | null }
  /** the user confirmed: close (with the overrides) or reopen */
  onConfirm: (overrides: FinYearAllocationOverride[] | undefined) => Promise<void>
  onCancel: () => void
}

const catKey = (id: number | null | undefined): string => (id == null ? 'unmapped' : String(id))

/** The override of a cell, by (partner, category), as the user has it now. */
type OverrideMap = Record<string, number | null>
const cellKey = (investorId: string, categoryId: number | null | undefined): string =>
  `${investorId}::${catKey(categoryId)}`

export default function CloseFinYearModal({ open, fundId, finYear, onConfirm, onCancel }: CloseFinYearModalProps) {
  const closing = !finYear.closed
  const currencyText = useNumberDisplay('currency')
  const currencyFmt = useNumberFormat('currency')
  const [allocation, setAllocation] = useState<FundFinYearAllocation | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<OverrideMap>({})
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !closing) {
      setAllocation(null)
      setOverrides({})
      setLoadError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    void fetchFundFinYearAllocation(fundId, finYear.id, token).then((res) => {
      if (cancelled) return
      setLoading(false)
      if (res.error || !res.data) {
        setLoadError(res.error ?? 'The allocation could not be read.')
        setAllocation(null)
        return
      }
      setLoadError(null)
      setAllocation(res.data)
      // the stored overrides come back on the cells
      const next: OverrideMap = {}
      res.data.rows.forEach((r) =>
        r.cells.forEach((c) => {
          if (c.prog__override != null) next[cellKey(r.investors__id, c.tax_categories__id)] = c.prog__override
        })
      )
      setOverrides(next)
    })
    return () => {
      cancelled = true
    }
  }, [open, closing, fundId, finYear.id])

  const perFinYear = Boolean(allocation?.per_fin_year)
  const categories: FinYearAllocationCategory[] = useMemo(() => allocation?.categories ?? [], [allocation])
  const rows: FinYearAllocationRow[] = useMemo(() => allocation?.rows ?? [], [allocation])

  const cellOf = useCallback(
    (row: FinYearAllocationRow, categoryId: number | null | undefined): FinYearAllocationCell | undefined =>
      row.cells.find((c) => catKey(c.tax_categories__id) === catKey(categoryId)),
    []
  )

  // per category: do the overrides keep the category at its total? The
  // server (fund__close_fin_year) sums override-else-calculated with the
  // UNROUNDED calculated amounts; the dialog only has them rounded to the
  // cent, and the engine's cents by largest remainder sit in its lines, so
  // re-summing the rounded cells reads a cent or more off every total
  // (backlog 1.22). So: with no override the RPC's own verdict
  // (prog__balanced); with overrides, they must net to zero against the
  // calculated amounts they replace, a cent of rounding allowed per
  // overridden cell; with every cell overridden, the overrides themselves
  // must read the total. The server remains the arbiter: its refusal shows
  // as the error toast.
  const categoryTotals = useMemo(
    () =>
      categories.map((c) => {
        const cells: Array<{ cell: FinYearAllocationCell; override: number | null }> = []
        rows.forEach((r) => {
          const cell = cellOf(r, c.tax_categories__id)
          if (cell) cells.push({ cell, override: overrides[cellKey(r.investors__id, cell.tax_categories__id)] ?? null })
        })
        const overriddenCells = cells.flatMap((x) => (x.override != null ? [{ calculated: x.cell.prog__calculated, override: x.override }] : []))
        const overridden = overriddenCells.length > 0
        let balanced: boolean
        let effective: number
        if (!overridden) {
          balanced = c.prog__balanced
          effective = c.prog__period_total
        } else if (overriddenCells.length === cells.length) {
          effective = overriddenCells.reduce((sum, x) => sum + x.override, 0)
          balanced = Math.abs(effective - c.prog__period_total) < 0.005
        } else {
          const residual = overriddenCells.reduce((sum, x) => sum + (x.override - x.calculated), 0)
          balanced = Math.abs(residual) < 0.005 * (overriddenCells.length + 1)
          effective = c.prog__period_total + residual
        }
        return {
          key: catKey(c.tax_categories__id),
          effective: Math.round(effective * 100) / 100,
          balanced,
          overridden
        }
      }),
    [categories, rows, cellOf, overrides]
  )
  const unbalanced = categoryTotals.filter((t) => !t.balanced)
  const unmapped = allocation?.unmapped_accounts ?? []

  const blockedReason: string | null = useMemo(() => {
    if (!closing || !perFinYear) return null
    if (unmapped.length > 0) {
      return `${unmapped.length} P&L account(s) with a balance have no tax category. Map them in the chart of accounts first.`
    }
    if (unbalanced.length > 0) {
      const names = unbalanced
        .map((t) => categories.find((c) => catKey(c.tax_categories__id) === t.key)?.tax_categories__label ?? t.key)
        .slice(0, 3)
      return `The overrides of ${names.join(', ')} do not add up to the category total.`
    }
    return null
  }, [closing, perFinYear, unmapped.length, unbalanced, categories])

  const handleOk = async () => {
    setBusy(true)
    try {
      if (!closing || !perFinYear) {
        await onConfirm(undefined)
        return
      }
      const list: FinYearAllocationOverride[] = []
      rows.forEach((r) =>
        r.cells.forEach((c) => {
          const o = overrides[cellKey(r.investors__id, c.tax_categories__id)]
          if (o != null) list.push({ investor_id: r.investors__id, tax_category_id: c.tax_categories__id ?? null, override_amount: o })
        })
      )
      await onConfirm(list)
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo(() => {
    const cols: Array<Record<string, unknown>> = [
      {
        title: 'Partner',
        dataIndex: 'investors__name',
        fixed: 'left' as const,
        width: 220,
        render: (v: unknown, row: FinYearAllocationRow) => (
          <span>
            {String(v ?? '-')}{' '}
            <Text type='secondary' className='text-xs'>
              {`${(row.prog__share * 100).toFixed(2)}%`}
            </Text>
          </span>
        )
      }
    ]
    categories.forEach((c) => {
      cols.push({
        title: (
          <span>
            {c.tax_categories__label}{' '}
            <Text type='secondary' className='text-xs'>
              {currencyText(c.prog__period_total)}
            </Text>
          </span>
        ),
        key: catKey(c.tax_categories__id),
        children: [
          {
            title: CALCULATED_TITLE,
            key: `${catKey(c.tax_categories__id)}__calculated`,
            width: 140,
            align: 'right' as const,
            render: (_: unknown, row: FinYearAllocationRow) => {
              const cell = cellOf(row, c.tax_categories__id)
              if (!cell) return <Text type='secondary'>—</Text>
              const calculated = currencyText(cell.prog__calculated)
              const o = overrides[cellKey(row.investors__id, cell.tax_categories__id)]
              const curative =
                Math.abs(cell.prog__curative) >= 0.005 ? (
                  <Tooltip title='Of which curative: the part that catches a partner up with earlier years, beyond its pro-rata share of this year'>
                    <Text type='secondary' className='block text-xs'>
                      {`${currencyText(cell.prog__curative)} curative`}
                    </Text>
                  </Tooltip>
                ) : null
              return (
                <span>
                  {o != null ? (
                    <Text delete type='secondary'>
                      {calculated}
                    </Text>
                  ) : (
                    calculated
                  )}
                  {curative}
                </span>
              )
            }
          },
          {
            title: OVERRIDE_TITLE,
            key: `${catKey(c.tax_categories__id)}__override`,
            width: 150,
            align: 'right' as const,
            render: (_: unknown, row: FinYearAllocationRow) => {
              const cell = cellOf(row, c.tax_categories__id)
              if (!cell) return <Text type='secondary'>—</Text>
              const k = cellKey(row.investors__id, cell.tax_categories__id)
              const o = overrides[k]
              return (
                <InputNumber
                  size='small'
                  className='w-full'
                  value={o ?? null}
                  {...currencyFmt}
                  placeholder='-'
                  disabled={Boolean(allocation?.is_booked)}
                  status={o != null ? 'warning' : undefined}
                  aria-label={`Override ${c.tax_categories__label} for ${row.investors__name}`}
                  onChange={(v) =>
                    setOverrides((prev) => {
                      const next = { ...prev }
                      if (v == null) delete next[k]
                      else next[k] = Number(v)
                      return next
                    })
                  }
                />
              )
            }
          }
        ]
      })
    })
    return cols
  }, [categories, cellOf, overrides, currencyText, currencyFmt, allocation?.is_booked])

  const outstanding = allocation?.outstanding ?? []

  const title = closing ? `Close FY ${finYear.fin_year}?` : `Reopen FY ${finYear.fin_year}?`
  const okText = closing ? 'Close year' : 'Reopen year'

  return (
    <Modal
      open={open}
      title={title}
      width={perFinYear && categories.length > 0 ? Math.min(1200, 320 + categories.length * 300) : 520}
      onCancel={onCancel}
      onOk={() => void handleOk()}
      okText={okText}
      // the AntD default footer [Cancel][Primary]; the primary is disabled
      // while something blocks and the Alert above the table says what
      okButtonProps={{ disabled: Boolean(blockedReason) || loading || Boolean(loadError), loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      destroyOnHidden
    >
      {!closing ? (
        <Text>Reopening removes the closing journals and the partner allocation lines the close booked, and unlocks the year.</Text>
      ) : loading ? (
        <Text type='secondary'>Loading the year’s allocation…</Text>
      ) : loadError ? (
        <Alert type='error' showIcon message='The allocation could not be read' description={loadError} />
      ) : !perFinYear ? (
        <Text>
          {allocation?.status_message ?? 'The fund shares its results on the Static basis: the close books no partner allocation.'}{' '}
          Closing books the year’s net income into Accumulated net income and locks the year against further bookings.
        </Text>
      ) : (
        <div className='flex flex-col gap-3'>
          {blockedReason && unmapped.length === 0 ? (
            <Alert type='warning' showIcon message='Close year is disabled' description={blockedReason} />
          ) : null}
          {unmapped.length > 0 ? (
            <Alert
              type='warning'
              showIcon
              message='Map every P&L account to a tax category before closing'
              description={
                <span>
                  {unmapped
                    .map((u) => `${u.acc_gl_accounts__account_code ? `${u.acc_gl_accounts__account_code} ` : ''}${u.acc_gl_accounts__account_name} (${u.prog__balance_fmt ?? currencyText(u.prog__balance)})`)
                    .join(', ')}
                  . The allocation is per K-1 tax category; an account without one cannot be allocated.
                </span>
              }
            />
          ) : null}
          <Text type='secondary'>
            {allocation?.status_message} Each partner takes of this year’s items, per tax category, what brings its cumulative
            allocation closest to its commitment share; the counter-lines leave every account at its total.
          </Text>
          {rows.length === 0 ? (
            <Text type='secondary'>No fund-level items to allocate this year.</Text>
          ) : (
            <Table
              size='small'
              columns={columns as never}
              dataSource={rows}
              rowKey='investors__id'
              pagination={false}
              scroll={{ x: 'max-content' }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row className='font-medium'>
                    <Table.Summary.Cell index={0}>Total (must read the category total)</Table.Summary.Cell>
                    {categories.map((c, i) => {
                      const t = categoryTotals[i]
                      return (
                        <React.Fragment key={catKey(c.tax_categories__id)}>
                          <Table.Summary.Cell index={1 + i * 2} align='right'>
                            {t.overridden ? (
                              <Text delete type='secondary'>
                                {currencyText(c.prog__period_total)}
                              </Text>
                            ) : (
                              currencyText(c.prog__period_total)
                            )}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2 + i * 2} align='right'>
                            {t.overridden ? (
                              <Text type={t.balanced ? undefined : 'danger'}>{currencyText(t.effective)}</Text>
                            ) : (
                              ''
                            )}
                          </Table.Summary.Cell>
                        </React.Fragment>
                      )
                    })}
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          )}
          {outstanding.length > 0 ? (
            <Alert
              type='warning'
              showIcon
              message={`Carried forward: ${outstanding.length} partner / tax category pair(s) could not be cured with this year’s items`}
              description={
                <Table
                  size='small'
                  pagination={false}
                  rowKey={(r: FundFinYearAllocation['outstanding'][number]) => `${r.investors__id}-${catKey(r.tax_categories__id)}`}
                  dataSource={outstanding}
                  columns={[
                    { title: 'Partner', dataIndex: 'investors__name' },
                    { title: 'Tax category', dataIndex: 'tax_categories__label' },
                    {
                      title: 'Carry-forward',
                      dataIndex: 'prog__carry_forward',
                      align: 'right' as const,
                      render: (v: number) => currencyText(v)
                    }
                  ]}
                />
              }
            />
          ) : null}
        </div>
      )}
    </Modal>
  )
}
